// frontend/src/modules/admin/components/WidgetMeteo.jsx
// Widget Météo Ferme - Open-Meteo API (gratuit, sans clé)
// Papy 73+ - Farm-Booking

import React, { useState, useEffect } from 'react';
import { 
  Sun, Cloud, CloudRain, CloudSnow, Wind, Droplets, Thermometer, 
  AlertTriangle, RefreshCw, MapPin, Sunrise, Sunset, Eye, Gauge,
  CloudLightning, CloudFog, Snowflake, TrendingDown, TrendingUp
} from 'lucide-react';

const WidgetMeteo = () => {
  // Configuration - À adapter selon la ferme
  const FARM_COORDS = {
    latitude: 49.33611443214846,  // Normandie par défaut
    longitude: 1.0261647022935751,
    name: "La Ferme du petit essart"
  };

  const [meteo, setMeteo] = useState(null);
  const [previsions, setPrevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  // ═══════════════════════════════════════════════════════════════════════════
  // CHARGEMENT DONNÉES OPEN-METEO
  // ═══════════════════════════════════════════════════════════════════════════

  const loadMeteo = async () => {
    try {
      setLoading(true);
      setError(null);

      // API Open-Meteo (gratuite, pas de clé requise)
      const url = `https://api.open-meteo.com/v1/forecast?` +
        `latitude=${FARM_COORDS.latitude}&longitude=${FARM_COORDS.longitude}` +
        `&current=temperature_2m,relative_humidity_2m,apparent_temperature,precipitation,rain,weather_code,cloud_cover,pressure_msl,wind_speed_10m,wind_direction_10m,wind_gusts_10m` +
        `&hourly=temperature_2m,precipitation_probability,precipitation,weather_code,wind_speed_10m` +
        `&daily=temperature_2m_max,temperature_2m_min,precipitation_sum,precipitation_probability_max,weather_code,sunrise,sunset` +
        `&timezone=Europe%2FParis` +
        `&forecast_days=3`;

      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Erreur API météo');
      }

      const data = await response.json();

      // Données actuelles
      setMeteo({
        temperature: Math.round(data.current.temperature_2m),
        temperatureRessentie: Math.round(data.current.apparent_temperature),
        humidite: data.current.relative_humidity_2m,
        precipitation: data.current.precipitation || 0,
        pluie: data.current.rain || 0,
        nuages: data.current.cloud_cover,
        pression: Math.round(data.current.pressure_msl),
        vent: Math.round(data.current.wind_speed_10m),
        rafales: Math.round(data.current.wind_gusts_10m),
        directionVent: data.current.wind_direction_10m,
        weatherCode: data.current.weather_code
      });

      // Prévisions journalières
      const daily = data.daily;
      const previsionsData = [];
      for (let i = 0; i < 3; i++) {
        previsionsData.push({
          date: new Date(daily.time[i]),
          tempMin: Math.round(daily.temperature_2m_min[i]),
          tempMax: Math.round(daily.temperature_2m_max[i]),
          precipitation: daily.precipitation_sum[i],
          precipitationProb: daily.precipitation_probability_max[i],
          weatherCode: daily.weather_code[i],
          sunrise: daily.sunrise[i],
          sunset: daily.sunset[i]
        });
      }
      setPrevisions(previsionsData);

      setLastUpdate(new Date());
      setLoading(false);

    } catch (err) {
      console.error('Erreur météo:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeteo();
    // Rafraîchir toutes les 15 minutes
    const interval = setInterval(loadMeteo, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  // Icône selon code météo WMO
  const getWeatherIcon = (code, size = 'h-8 w-8') => {
    // Codes WMO: https://open-meteo.com/en/docs
    if (code === 0) return <Sun className={`${size} text-yellow-500`} />;
    if (code <= 3) return <Cloud className={`${size} text-gray-400`} />;
    if (code <= 49) return <CloudFog className={`${size} text-gray-500`} />;
    if (code <= 59) return <CloudRain className={`${size} text-blue-400`} />;
    if (code <= 69) return <CloudSnow className={`${size} text-blue-200`} />;
    if (code <= 79) return <CloudSnow className={`${size} text-white`} />;
    if (code <= 84) return <CloudRain className={`${size} text-blue-500`} />;
    if (code <= 94) return <CloudSnow className={`${size} text-blue-300`} />;
    if (code <= 99) return <CloudLightning className={`${size} text-yellow-400`} />;
    return <Cloud className={`${size} text-gray-400`} />;
  };

  // Description météo
  const getWeatherDescription = (code) => {
    if (code === 0) return 'Ensoleillé';
    if (code <= 3) return 'Partiellement nuageux';
    if (code <= 49) return 'Brouillard';
    if (code <= 59) return 'Bruine';
    if (code <= 69) return 'Pluie';
    if (code <= 79) return 'Neige';
    if (code <= 84) return 'Averses';
    if (code <= 94) return 'Averses de neige';
    if (code <= 99) return 'Orage';
    return 'Variable';
  };

  // Direction du vent
  const getWindDirection = (degrees) => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'];
    const index = Math.round(degrees / 45) % 8;
    return directions[index];
  };

  // Calcul indice gel simplifié
  const getIndiceGel = () => {
    if (!meteo) return { score: 0, label: 'N/A', color: 'gray' };
    
    let score = 0;
    const temp = meteo.temperature;
    const vent = meteo.vent;
    const nuages = meteo.nuages;
    const humidite = meteo.humidite;

    // Température
    if (temp <= 0) score += 40;
    else if (temp <= 2) score += 30;
    else if (temp <= 5) score += 15;

    // Vent faible = risque accru
    if (vent < 5) score += 20;
    else if (vent < 10) score += 10;

    // Ciel clair = risque accru (refroidissement radiatif)
    if (nuages < 20) score += 20;
    else if (nuages < 50) score += 10;

    // Humidité élevée = risque gelée blanche
    if (humidite > 80) score += 10;

    // Vérifier les prévisions nuit
    if (previsions.length > 0 && previsions[0].tempMin <= 2) {
      score += 10;
    }

    score = Math.min(100, score);

    if (score >= 70) return { score, label: 'ÉLEVÉ', color: 'red' };
    if (score >= 50) return { score, label: 'MODÉRÉ', color: 'orange' };
    if (score >= 30) return { score, label: 'FAIBLE', color: 'yellow' };
    return { score, label: 'AUCUN', color: 'green' };
  };

  // Format date
  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' });
  };

  const formatTime = (isoString) => {
    return new Date(isoString).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  if (loading && !meteo) {
    return (
      <div className="bg-white p-8 rounded-lg shadow flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mr-3" />
        <span className="text-gray-600">Chargement météo...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white p-8 rounded-lg shadow">
        <div className="text-center text-red-500">
          <AlertTriangle className="h-12 w-12 mx-auto mb-4" />
          <p className="font-medium">Erreur chargement météo</p>
          <p className="text-sm text-gray-500 mt-2">{error}</p>
          <button 
            onClick={loadMeteo}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const indiceGel = getIndiceGel();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-blue-800 flex items-center gap-2">
          🌤️ Météo Ferme
        </h2>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">
            Maj: {lastUpdate?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </span>
          <button
            onClick={loadMeteo}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* MÉTÉO ACTUELLE */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-center gap-2 mb-4 text-blue-100">
          <MapPin className="h-4 w-4" />
          <span className="text-sm">{FARM_COORDS.name} • Données Meteo</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Température principale */}
          <div className="flex items-center gap-4">
            {getWeatherIcon(meteo.weatherCode, 'h-16 w-16')}
            <div>
              <div className="text-5xl font-bold">{meteo.temperature}°C</div>
              <div className="text-blue-200">
                Ressenti {meteo.temperatureRessentie}°C
              </div>
              <div className="text-sm text-blue-100 mt-1">
                {getWeatherDescription(meteo.weatherCode)}
              </div>
            </div>
          </div>

          {/* Détails */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/10 rounded-lg p-3">
              <div className="flex items-center gap-2 text-blue-200 text-sm">
                <Droplets className="h-4 w-4" />
                Humidité
              </div>
              <div className="text-xl font-semibold">{meteo.humidite}%</div>
            </div>
            <div className="bg-white/10 rounded-lg p-3">
              <div className="flex items-center gap-2 text-blue-200 text-sm">
                <Wind className="h-4 w-4" />
                Vent
              </div>
              <div className="text-xl font-semibold">
                {meteo.vent} km/h
                <span className="text-sm text-blue-200 ml-1">{getWindDirection(meteo.directionVent)}</span>
              </div>
            </div>
            <div className="bg-white/10 rounded-lg p-3">
              <div className="flex items-center gap-2 text-blue-200 text-sm">
                <CloudRain className="h-4 w-4" />
                Pluie
              </div>
              <div className="text-xl font-semibold">{meteo.pluie} mm</div>
            </div>
            <div className="bg-white/10 rounded-lg p-3">
              <div className="flex items-center gap-2 text-blue-200 text-sm">
                <Gauge className="h-4 w-4" />
                Pression
              </div>
              <div className="text-xl font-semibold">{meteo.pression} hPa</div>
            </div>
          </div>

          {/* Indice Gel */}
          <div className={`rounded-lg p-4 ${
            indiceGel.color === 'red' ? 'bg-red-500' :
            indiceGel.color === 'orange' ? 'bg-orange-500' :
            indiceGel.color === 'yellow' ? 'bg-yellow-500' :
            'bg-green-500'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <Snowflake className="h-5 w-5" />
              <span className="font-medium">Risque de Gel</span>
            </div>
            <div className="text-4xl font-bold">{indiceGel.score}/100</div>
            <div className="text-lg font-semibold mt-1">{indiceGel.label}</div>
            {indiceGel.score >= 50 && (
              <div className="mt-2 text-sm bg-white/20 rounded p-2">
                ⚠️ Surveiller les cultures sensibles
              </div>
            )}
          </div>
        </div>

        {/* Rafales & Nuages */}
        <div className="flex gap-6 mt-4 pt-4 border-t border-white/20 text-sm">
          <div className="flex items-center gap-2">
            <Wind className="h-4 w-4 text-blue-200" />
            <span className="text-blue-200">Rafales:</span>
            <span className="font-medium">{meteo.rafales} km/h</span>
          </div>
          <div className="flex items-center gap-2">
            <Cloud className="h-4 w-4 text-blue-200" />
            <span className="text-blue-200">Couverture:</span>
            <span className="font-medium">{meteo.nuages}%</span>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* PRÉVISIONS 3 JOURS */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {previsions.map((jour, index) => (
          <div 
            key={index} 
            className={`bg-white rounded-lg p-5 border shadow-sm ${
              index === 0 ? 'border-blue-300 border-2' : ''
            }`}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="font-semibold text-gray-900">
                  {index === 0 ? "Aujourd'hui" : formatDate(jour.date)}
                </div>
                <div className="text-sm text-gray-500">
                  {getWeatherDescription(jour.weatherCode)}
                </div>
              </div>
              {getWeatherIcon(jour.weatherCode, 'h-10 w-10')}
            </div>

            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendingDown className="h-4 w-4 text-blue-500" />
                <span className="text-lg font-medium text-blue-600">{jour.tempMin}°</span>
              </div>
              <div className="flex-1 h-2 bg-gradient-to-r from-blue-200 via-yellow-200 to-red-200 rounded mx-3" />
              <div className="flex items-center gap-2">
                <span className="text-lg font-medium text-red-500">{jour.tempMax}°</span>
                <TrendingUp className="h-4 w-4 text-red-500" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <CloudRain className="h-4 w-4 text-blue-400" />
                <span>{jour.precipitation} mm</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Droplets className="h-4 w-4 text-blue-400" />
                <span>{jour.precipitationProb}%</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Sunrise className="h-4 w-4 text-orange-400" />
                <span>{formatTime(jour.sunrise)}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Sunset className="h-4 w-4 text-orange-500" />
                <span>{formatTime(jour.sunset)}</span>
              </div>
            </div>

            {/* Alerte gel si min <= 2°C */}
            {jour.tempMin <= 2 && (
              <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-sm text-blue-700">
                <Snowflake className="h-4 w-4" />
                <span>Risque de gel nocturne</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* ALERTES */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Alerte Gel */}
        <div className={`p-4 rounded-lg border-2 ${
          indiceGel.score >= 50 
            ? 'bg-red-50 border-red-300' 
            : 'bg-green-50 border-green-300'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <Snowflake className={`h-5 w-5 ${
              indiceGel.score >= 50 ? 'text-red-600' : 'text-green-600'
            }`} />
            <span className="font-medium">Gel</span>
          </div>
          <div className={`text-lg font-bold ${
            indiceGel.score >= 50 ? 'text-red-700' : 'text-green-700'
          }`}>
            {indiceGel.score >= 50 ? '⚠️ Vigilance' : '✅ RAS'}
          </div>
        </div>

        {/* Alerte Vent */}
        <div className={`p-4 rounded-lg border-2 ${
          meteo.rafales > 60 
            ? 'bg-orange-50 border-orange-300' 
            : 'bg-green-50 border-green-300'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <Wind className={`h-5 w-5 ${
              meteo.rafales > 60 ? 'text-orange-600' : 'text-green-600'
            }`} />
            <span className="font-medium">Vent</span>
          </div>
          <div className={`text-lg font-bold ${
            meteo.rafales > 60 ? 'text-orange-700' : 'text-green-700'
          }`}>
            {meteo.rafales > 60 ? `⚠️ Rafales ${meteo.rafales} km/h` : '✅ Calme'}
          </div>
        </div>

        {/* Alerte Pluie */}
        <div className={`p-4 rounded-lg border-2 ${
          meteo.pluie > 5 
            ? 'bg-blue-50 border-blue-300' 
            : 'bg-green-50 border-green-300'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <CloudRain className={`h-5 w-5 ${
              meteo.pluie > 5 ? 'text-blue-600' : 'text-green-600'
            }`} />
            <span className="font-medium">Pluie</span>
          </div>
          <div className={`text-lg font-bold ${
            meteo.pluie > 5 ? 'text-blue-700' : 'text-green-700'
          }`}>
            {meteo.pluie > 5 ? `🌧️ ${meteo.pluie} mm/h` : '✅ Sec'}
          </div>
        </div>

        {/* Pression tendance */}
        <div className={`p-4 rounded-lg border-2 ${
          meteo.pression < 1010 
            ? 'bg-yellow-50 border-yellow-300' 
            : 'bg-green-50 border-green-300'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <Gauge className={`h-5 w-5 ${
              meteo.pression < 1010 ? 'text-yellow-600' : 'text-green-600'
            }`} />
            <span className="font-medium">Pression</span>
          </div>
          <div className={`text-lg font-bold ${
            meteo.pression < 1010 ? 'text-yellow-700' : 'text-green-700'
          }`}>
            {meteo.pression < 1010 ? '📉 Dépression' : '📈 Anticyclone'}
          </div>
        </div>
      </div>

      {/* Footer info */}
      <div className="text-center text-sm text-gray-400">
        Données fournies par Open-Meteo.com • Mises à jour toutes les 15 minutes
      </div>
    </div>
  );
};

export default WidgetMeteo;